import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShieldCheck, Users, Lock, Headphones, FileText, BarChart3, Zap, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { notify } from "../lib/notify";
import SEO from "../components/SEO";

const ENTERPRISE_FEATURES = [
  { Icon: Users, title: "Unlimited team seats", body: "Pool unlimited DPOs, CROs, internal auditors and BD into one workspace. Per-seat SSO." },
  { Icon: Lock, title: "SSO & SCIM provisioning", body: "Okta · Azure AD · Google Workspace. Automated joiner/leaver flows. Role-based access on every asset." },
  { Icon: ShieldCheck, title: "Singapore data residency", body: "All your assessments, workflow outputs and templates stored in ap-southeast-1. MAS Outsourcing Notice compliant." },
  { Icon: FileText, title: "Co-branded report exports", body: "Your logo on every .xlsx · .docx · .pdf. Custom cover sheets, signature blocks, and footers." },
  { Icon: BarChart3, title: "Org-wide dashboard", body: "Aggregate CSRS, PDPA, MAS TRM scores across business units. Track drift quarter-on-quarter for board reporting." },
  { Icon: Headphones, title: "Dedicated success manager", body: "Singapore-based onboarding, quarterly business reviews and direct line to our risk research team." },
  { Icon: Zap, title: "Bot integrations", body: "Slack, MS Teams, Telegram and WhatsApp — pipe workflow results to where your team already chats." },
  { Icon: FileText, title: "Audit log + retention controls", body: "Immutable activity log, configurable retention, and SOC 2-ready evidence pack." },
];

export default function Enterprise() {
  const [form, setForm] = useState({ name: "", work_email: "", company: "", role: "CRO", seats: 10, use_case: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.work_email || !form.company) { notify.error("Name, work email and company required."); return; }
    setBusy(true);
    try {
      await api.post("/enterprise/lead", form);
      setSent(true);
      notify.success("Got it — we'll be in touch within one business day.");
    } catch (e2) { notify.error(e2.response?.data?.detail || "Could not submit."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="page-enterprise">
      <SEO
        title="Enterprise · Singapore-grade GRC platform for FIs, insurers and PIs"
        description="RiskPedia Singapore for Enterprise — SSO, SCIM, SG data residency, co-branded reports, org dashboard, dedicated success manager. Built for Singapore FIs that need to scale GRC across business units."
        keywords={["Singapore enterprise GRC", "SSO compliance Singapore", "MAS outsourcing platform", "enterprise risk Singapore"]}
        path="/enterprise"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "RiskPedia SG Enterprise",
          description: "Enterprise-grade Singapore risk and compliance platform.",
          offers: { "@type": "Offer", priceCurrency: "SGD", price: "Contact us" },
          provider: { "@type": "Organization", name: "RiskPedia Singapore" },
        }}
      />

      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">Enterprise · for FIs that scale</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Singapore GRC,<br /><span style={{ color: "var(--rp-accent)" }}>built for boards.</span></h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">RiskPedia Pro covers individual practitioners. Enterprise is for the whole organisation — SSO, audit logs, data residency, co-branded reports and a Singapore-based success team. Pricing scales with seats, starting at S$1,990 / month for 25 seats.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#contact" className="btn-accent">Talk to our team <ArrowRight size={14} /></a>
            <Link to="/pricing" className="btn-outline">Compare with Pro</Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          <div className="overline">What you get</div>
          <h2 className="font-display text-3xl font-semibold mt-3">Everything in Pro · plus 8 things only Enterprise needs.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mt-10">
            {ENTERPRISE_FEATURES.map(({ Icon, title, body }) => (
              <div key={title} className="rp-surface p-6" data-testid={`ent-feature-${title.split(" ")[0].toLowerCase()}`}>
                <Icon size={22} style={{ color: "var(--rp-accent)" }} />
                <div className="font-display text-base font-semibold mt-4">{title}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-slate-50 border-y border-slate-200">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline">For who</div>
            <h2 className="font-display text-3xl font-semibold mt-3">Built for Singapore's regulated economy.</h2>
            <ul className="mt-6 space-y-3 text-sm text-slate-600">
              {[
                "MAS-licensed banks, insurers, capital markets services & payment institutions",
                "Multi-entity holding companies needing consolidated risk reporting",
                "GRC consulting firms running client engagements at scale",
                "CII operators under CCoP 2.0",
                "AI-first fintechs needing MAS MindForge / Veritas FEAT operational evidence",
              ].map(b => <li key={b} className="flex items-start gap-2"><Check size={14} style={{ color: "var(--rp-accent)" }} className="mt-0.5 flex-shrink-0" /> {b}</li>)}
            </ul>
            <div className="mt-8 rp-card p-5">
              <div className="overline-muted">Indicative pricing</div>
              <div className="font-display text-3xl font-bold mt-2" style={{ color: "var(--rp-primary)" }}>From S$1,990<span className="text-sm text-slate-500">/mo · 25 seats</span></div>
              <p className="text-xs text-slate-500 mt-3">Annual contracts. Volume discount above 100 seats. Custom training and white-glove migration included on annual plans.</p>
            </div>
          </div>
          <div id="contact" className="lg:col-span-7">
            <div className="rp-card p-8" data-testid="ent-form">
              <div className="overline">Talk to us</div>
              <h3 className="font-display text-2xl font-semibold mt-3">Get a tailored proposal in 24 hours.</h3>
              {sent ? (
                <div className="mt-8 text-center py-12">
                  <div className="overline" style={{ color: "var(--rp-accent)" }}>Thanks — message received</div>
                  <h4 className="font-display text-2xl font-bold mt-3">We'll be in touch.</h4>
                  <p className="text-slate-600 mt-3">A Singapore-based teammate will email you within one business day with a tailored proposal and a 30-min calendar link.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ent-name" required />
                    <Input label="Work email" type="email" value={form.work_email} onChange={(v) => setForm({ ...form, work_email: v })} testid="ent-email" required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="ent-company" required />
                    <div>
                      <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Role</label>
                      <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="ent-role">
                        {["CRO", "CISO", "DPO", "Head of Compliance", "Head of Internal Audit", "Head of Risk", "GRC Consultant", "C-Suite", "Other"].map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Seats needed</label>
                    <input type="number" min="5" value={form.seats} onChange={(e) => setForm({ ...form, seats: Number(e.target.value) })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="ent-seats" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Use case (optional)</label>
                    <textarea rows={3} value={form.use_case} onChange={(e) => setForm({ ...form, use_case: e.target.value })} placeholder="What problem are you trying to solve? Specific MAS notice you're preparing for? Number of business units?" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid="ent-usecase" />
                  </div>
                  <button type="submit" disabled={busy} className="btn-accent w-full justify-center" data-testid="ent-submit">{busy ? "Sending…" : "Request proposal"} <ArrowRight size={14} /></button>
                  <p className="text-[10px] text-slate-500 text-center">We'll never share your details. Read our privacy policy.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">{label}{required && <span className="text-rose-600 ml-1">*</span>}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2" data-testid={testid} required={required} />
    </div>
  );
}
